import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { encryptData } from "@excalidraw/excalidraw/data/encryption";
import { newElementWith } from "@excalidraw/element";
import throttle from "lodash.throttle";

import type { UserIdleState } from "@excalidraw/common";
import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type {
  OnUserFollowedPayload,
  SocketId,
} from "@excalidraw/excalidraw/types";

import { WS_EVENTS, FILE_UPLOAD_TIMEOUT, WS_SUBTYPES } from "../app_constants";
import { isSyncableElement } from "../data";

import type {
  SocketUpdateData,
  SocketUpdateDataSource,
  SyncableExcalidrawElement,
} from "../data";
import type { TCollabClass } from "./Collab";

/**
 * Wraps a raw WebSocket to match the event interface that Collab.tsx expects.
 * Replaces socket.io-client.
 */
class Portal {
  collab: TCollabClass;
  socket: WebSocket | null = null;
  socketInitialized: boolean = false;
  roomId: string | null = null;
  roomKey: string | null = null;
  broadcastedElementVersions: Map<string, number> = new Map();

  /** The server-assigned socket ID for this client */
  id: string | null = null;

  /** Event listeners registered via on() */
  private listeners: Map<string, Set<(...args: any[]) => void>> = new Map();
  /** One-time listeners registered via once() */
  private onceListeners: Map<string, Set<(...args: any[]) => void>> = new Map();

  /** Reconnection state */
  private wsUrl: string | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;
  private static MAX_RECONNECT_ATTEMPTS = 10;
  private static RECONNECT_BASE_MS = 500;
  private static RECONNECT_MAX_MS = 30000;

  constructor(collab: TCollabClass) {
    this.collab = collab;
  }

  // ---- Event emitter helpers ----

  on(event: string, fn: (...args: any[]) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(fn);
  }

  once(event: string, fn: (...args: any[]) => void) {
    if (!this.onceListeners.has(event)) {
      this.onceListeners.set(event, new Set());
    }
    this.onceListeners.get(event)!.add(fn);
  }

  off(event: string, fn: (...args: any[]) => void) {
    this.listeners.get(event)?.delete(fn);
    this.onceListeners.get(event)?.delete(fn);
  }

  private emit(event: string, ...args: any[]) {
    for (const fn of this.listeners.get(event) || []) {
      fn(...args);
    }
    const onceFns = this.onceListeners.get(event);
    if (onceFns) {
      for (const fn of onceFns) {
        fn(...args);
      }
      onceFns.clear();
    }
  }

  // ---- WebSocket lifecycle ----

  open(ws: WebSocket, id: string, key: string, wsUrl?: string): Portal {
    this.socket = ws;
    this.roomId = id;
    this.roomKey = key;
    this.intentionallyClosed = false;
    if (wsUrl) {
      this.wsUrl = wsUrl;
    }

    this.attachSocketListeners(ws);

    return this;
  }

  private attachSocketListeners(ws: WebSocket) {
    ws.addEventListener("message", (event) => {
      let msg: any;
      try {
        msg = JSON.parse(event.data as string);
      } catch {
        return;
      }

      switch (msg.type) {
        case "init-room":
          // Server assigned us a socketId — don't join yet.
          // Collab must call portal.joinRoom() after registering listeners.
          this.id = msg.socketId;
          this.reconnectAttempts = 0; // successful connection
          this.emit("init-room");
          break;

        case "first-in-room":
          this.emit("first-in-room");
          break;

        case "new-user":
          this.broadcastScene(
            WS_SUBTYPES.INIT,
            this.collab.getSceneElementsIncludingDeleted(),
            /* syncAll */ true,
          );
          break;

        case "room-user-change":
          this.collab.setCollaborators(msg.clients as SocketId[]);
          break;

        case "client-broadcast":
          this.emit(
            "client-broadcast",
            this.arrayToBuffer(msg.data),
            new Uint8Array(msg.iv),
          );
          break;

        case "user-follow-room-change":
          this.emit(WS_EVENTS.USER_FOLLOW_ROOM_CHANGE, msg.payload);
          break;
      }
    });

    ws.addEventListener("error", () => {
      this.emit("connect_error");
    });

    ws.addEventListener("close", () => {
      if (!this.intentionallyClosed && this.wsUrl && this.roomId && this.roomKey) {
        this.scheduleReconnect();
      }
    });
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= Portal.MAX_RECONNECT_ATTEMPTS) {
      console.error("Excalidraw collab: max reconnect attempts reached");
      this.emit("connect_error");
      return;
    }

    const delay = Math.min(
      Portal.RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts),
      Portal.RECONNECT_MAX_MS,
    );
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      if (!this.wsUrl || !this.roomId || !this.roomKey) return;

      try {
        const ws = new WebSocket(this.wsUrl);
        this.socket = ws;
        this.id = null;
        this.attachSocketListeners(ws);

        // On reconnect, auto-join since Collab's listeners are already registered
        this.once("init-room", () => {
          this.joinRoom();
          // Re-broadcast full scene to sync with any peers
          this.broadcastScene(
            WS_SUBTYPES.INIT,
            this.collab.getSceneElementsIncludingDeleted(),
            true,
          );
        });
      } catch {
        this.scheduleReconnect();
      }
    }, delay);
  }

  close() {
    if (!this.socket) {
      return;
    }
    this.intentionallyClosed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.queueFileUpload.flush();
    this.socket.close();
    this.socket = null;
    this.roomId = null;
    this.roomKey = null;
    this.wsUrl = null;
    this.id = null;
    this.socketInitialized = false;
    this.reconnectAttempts = 0;
    this.broadcastedElementVersions = new Map();
    this.listeners.clear();
    this.onceListeners.clear();
  }

  /**
   * Called by Collab after it has registered all its event listeners.
   * This sends the join-room message to the server, which triggers
   * first-in-room / new-user / room-user-change responses.
   */
  joinRoom() {
    if (this.socket?.readyState === WebSocket.OPEN && this.roomId) {
      this.socket.send(
        JSON.stringify({ type: "join-room", roomId: this.roomId }),
      );
      trackEvent("share", "room joined");
    }
  }

  isOpen() {
    return !!(
      this.socketInitialized &&
      this.socket &&
      this.socket.readyState === WebSocket.OPEN &&
      this.roomId &&
      this.roomKey
    );
  }

  private arrayToBuffer(data: number[] | ArrayBuffer): ArrayBuffer {
    if (data instanceof ArrayBuffer) return data;
    return new Uint8Array(data).buffer as ArrayBuffer;
  }

  async _broadcastSocketData(
    data: SocketUpdateData,
    volatile: boolean = false,
    roomId?: string,
  ) {
    if (this.isOpen()) {
      const json = JSON.stringify(data);
      const encoded = new TextEncoder().encode(json);
      const { encryptedBuffer, iv } = await encryptData(this.roomKey!, encoded);

      this.socket!.send(
        JSON.stringify({
          type: volatile
            ? WS_EVENTS.SERVER_VOLATILE
            : WS_EVENTS.SERVER,
          roomId: roomId ?? this.roomId,
          data: Array.from(new Uint8Array(encryptedBuffer)),
          iv: Array.from(iv),
        }),
      );
    }
  }

  queueFileUpload = throttle(async () => {
    try {
      await this.collab.fileManager.saveFiles({
        elements: this.collab.excalidrawAPI.getSceneElementsIncludingDeleted(),
        files: this.collab.excalidrawAPI.getFiles(),
      });
    } catch (error: any) {
      if (error.name !== "AbortError") {
        this.collab.excalidrawAPI.updateScene({
          appState: {
            errorMessage: error.message,
          },
        });
      }
    }

    let isChanged = false;
    const newElements = this.collab.excalidrawAPI
      .getSceneElementsIncludingDeleted()
      .map((element) => {
        if (this.collab.fileManager.shouldUpdateImageElementStatus(element)) {
          isChanged = true;
          return newElementWith(element, { status: "saved" });
        }
        return element;
      });

    if (isChanged) {
      this.collab.excalidrawAPI.updateScene({
        elements: newElements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }
  }, FILE_UPLOAD_TIMEOUT);

  broadcastScene = async (
    updateType: WS_SUBTYPES.INIT | WS_SUBTYPES.UPDATE,
    elements: readonly OrderedExcalidrawElement[],
    syncAll: boolean,
  ) => {
    if (updateType === WS_SUBTYPES.INIT && !syncAll) {
      throw new Error("syncAll must be true when sending SCENE.INIT");
    }

    const syncableElements = elements.reduce((acc, element) => {
      if (
        (syncAll ||
          !this.broadcastedElementVersions.has(element.id) ||
          element.version > this.broadcastedElementVersions.get(element.id)!) &&
        isSyncableElement(element)
      ) {
        acc.push(element);
      }
      return acc;
    }, [] as SyncableExcalidrawElement[]);

    const data: SocketUpdateDataSource[typeof updateType] = {
      type: updateType,
      payload: {
        elements: syncableElements,
      },
    };

    for (const syncableElement of syncableElements) {
      this.broadcastedElementVersions.set(
        syncableElement.id,
        syncableElement.version,
      );
    }

    this.queueFileUpload();

    await this._broadcastSocketData(data as SocketUpdateData);
  };

  broadcastIdleChange = (userState: UserIdleState) => {
    if (this.id) {
      const data: SocketUpdateDataSource["IDLE_STATUS"] = {
        type: WS_SUBTYPES.IDLE_STATUS,
        payload: {
          socketId: this.id as SocketId,
          userState,
          username: this.collab.state.username,
        },
      };
      return this._broadcastSocketData(
        data as SocketUpdateData,
        true, // volatile
      );
    }
  };

  broadcastMouseLocation = (payload: {
    pointer: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["pointer"];
    button: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["button"];
  }) => {
    if (this.id) {
      const data: SocketUpdateDataSource["MOUSE_LOCATION"] = {
        type: WS_SUBTYPES.MOUSE_LOCATION,
        payload: {
          socketId: this.id as SocketId,
          pointer: payload.pointer,
          button: payload.button || "up",
          selectedElementIds:
            this.collab.excalidrawAPI.getAppState().selectedElementIds,
          username: this.collab.state.username,
        },
      };

      return this._broadcastSocketData(
        data as SocketUpdateData,
        true, // volatile
      );
    }
  };

  broadcastVisibleSceneBounds = (
    payload: {
      sceneBounds: SocketUpdateDataSource["USER_VISIBLE_SCENE_BOUNDS"]["payload"]["sceneBounds"];
    },
    roomId: string,
  ) => {
    if (this.id) {
      const data: SocketUpdateDataSource["USER_VISIBLE_SCENE_BOUNDS"] = {
        type: WS_SUBTYPES.USER_VISIBLE_SCENE_BOUNDS,
        payload: {
          socketId: this.id as SocketId,
          username: this.collab.state.username,
          sceneBounds: payload.sceneBounds,
        },
      };

      return this._broadcastSocketData(
        data as SocketUpdateData,
        true, // volatile
        roomId,
      );
    }
  };

  broadcastUserFollowed = (payload: OnUserFollowedPayload) => {
    if (this.id && this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(
        JSON.stringify({
          type: WS_EVENTS.USER_FOLLOW_CHANGE,
          payload,
        }),
      );
    }
  };
}

export default Portal;
